import 'isomorphic-fetch'; /* global fetch:false */
import { partition, arrayToObject } from '../utility/utility';

export function loadAllListings() {
  return fetch('/nestiolistings')
    .then((response) => {
      if (!response.ok)
        // throw new Error("Nestio reported an error."); // This is a bad error message, instead we should log the response and throw a more informative error.
        // nothing after throw is executed, so response.json() will not be called, instead we should log the response and throw a more informative error.
        console.error('Nestio reported an error.', response);

      return response.json();
    })
    .then((json) => {
      return nestioListingsToModelListings(json.items);
    })
    .catch((err) => {
      return err;
    });
}

// Preloaded listings from the server won't have accurate information about
// which listings are saved, because that's stored client side in a cookie.
// So, when we get preloaded listings, we have to transform them to have the
// accurate saved information by running them through this function.
export function recomputeSavedListings(preloadedListings) {
  const savedListingsById = arrayToObject((it) => it, getSavedListings());

  return Object.keys(preloadedListings).reduce((updatedListings, id) => {
    updatedListings[id] = {
      ...preloadedListings[id],
      isSaved: Boolean(savedListingsById[id]),
    };

    return updatedListings;
  }, {});
}

export function nestioListingsToModelListings(items) {
  const savedListingsById = arrayToObject((it) => it, getSavedListings());
  const listingsByBedroomCount = partition('bedrooms', items);
  console.log('Items', items);

  try {
    return items.reduce((acc, it) => {
      // Pull out floorplan photos so we can exclude them from main photos list
      let { true: floorplanPhotos = [], false: normalPhotos = [] } = partition(
        (photo) => photo.media_type === 'Floor plan',
        it.photos
      );
      console.log('it', it);

      // Coerce nestio json into the standard form for our model
      acc[it.id] = {
        // straight copies or simple renames
        id: it.id,
        amenities: it.amenities,
        layoutName: it.layout,
        unitNumber: it.unit_number,
        bedroomCount: it.bedrooms,
        bathroomCount: it.bathrooms,
        sqft: it.square_footage,
        description: it.description,
        private: it.private,
        unitTypeName: it.unit_type && it.unit_type.name,

        // type conversion/formatting/derived data
        moveInDate: new Date(it.date_available),
        price:
          typeof it.price !== 'undefined' && !isNaN(it.price)
            ? parseInt(it.price)
            : '',
        showAsterisk: it.is_net_effective_price ?  '*' :'',
        photos: normalPhotos,
        floorplanUrl: floorplanPhotos
          .map((it) => it.original || it.xlarge || it.large)
          .filter((url) => url !== undefined)[0],
        imageUrl: normalPhotos[0].original || floorplanPhotos[0].original,
        similar: listingsByBedroomCount[it.bedrooms]
          .map((it) => it.id)
          .filter((id) => id != it.id),

        // external data
        h5Url: unitNumbersToH5Url[it.unit_number],
        isSaved: Boolean(savedListingsById[it.id]),
      };
      console.log('Items Reduced', acc);
      return acc;
    }, {});
  } catch (err) {
    return err;
    // console.log('An error occured with Nestio.');
  }
}

export function markListingAsSaved(id) {
  if (!isListingSaved(id)) {
    setSavedListings(getSavedListings().concat([id]));
  }
}

export function unmarkListingAsSaved(id) {
  setSavedListings(getSavedListings().filter((it) => it !== id));
}

export function isListingSaved(id) {
  return getSavedListings().includes(id);
}

const unitNumbersToH5Url = {
  '10A': 'https://one.h5property.com/buildings/bam/apartments/47',
  '10B': 'https://one.h5property.com/buildings/bam/apartments/5',
  '10C': 'https://one.h5property.com/buildings/bam/apartments/17',
  '10D': 'https://one.h5property.com/buildings/bam/apartments/37',
  '10E': 'https://one.h5property.com/buildings/bam/apartments/35',
  '10F': 'https://one.h5property.com/buildings/bam/apartments/16',
  '10J': 'https://one.h5property.com/buildings/bam/apartments/6',
  '10K': 'https://one.h5property.com/buildings/bam/apartments/34',
  '10M': 'https://one.h5property.com/buildings/bam/apartments/33',
  '10Q': 'https://one.h5property.com/buildings/bam/apartments/71',
  '10R': 'https://one.h5property.com/buildings/bam/apartments/21',
  '10U': 'https://one.h5property.com/buildings/bam/apartments/23',
  '11A': 'https://one.h5property.com/buildings/bam/apartments/306',
  '11B': 'https://one.h5property.com/buildings/bam/apartments/305',
  '11C': 'https://one.h5property.com/buildings/bam/apartments/15',
  '11D': 'https://one.h5property.com/buildings/bam/apartments/304',
  '11E': 'https://one.h5property.com/buildings/bam/apartments/7',
  '11J': 'https://one.h5property.com/buildings/bam/apartments/32',
  '11K': 'https://one.h5property.com/buildings/bam/apartments/31',
  '11M': 'https://one.h5property.com/buildings/bam/apartments/30',
  '11Q': 'https://one.h5property.com/buildings/bam/apartments/36',
  '11R': 'https://one.h5property.com/buildings/bam/apartments/72',
  '11T': 'https://one.h5property.com/buildings/bam/apartments/66',
  '11U': 'https://one.h5property.com/buildings/bam/apartments/70',
  '12A': 'https://one.h5property.com/buildings/bam/apartments/49',
  '12B': 'https://one.h5property.com/buildings/bam/apartments/40',
  '12C': 'https://one.h5property.com/buildings/bam/apartments/14',
  '12D': 'https://one.h5property.com/buildings/bam/apartments/39',
  '12E': 'https://one.h5property.com/buildings/bam/apartments/29',
  '12F': 'https://one.h5property.com/buildings/bam/apartments/8',
  '12J': 'https://one.h5property.com/buildings/bam/apartments/28',
  '12K': 'https://one.h5property.com/buildings/bam/apartments/27',
  '12M': 'https://one.h5property.com/buildings/bam/apartments/9',
  '12Q': 'https://one.h5property.com/buildings/bam/apartments/13',
  '12R': 'https://one.h5property.com/buildings/bam/apartments/123',
  '12T': 'https://one.h5property.com/buildings/bam/apartments/41',
  '12U': 'https://one.h5property.com/buildings/bam/apartments/136',
  '14A': 'https://one.h5property.com/buildings/bam/apartments/210',
  '14B': 'https://one.h5property.com/buildings/bam/apartments/267',
  '14C': 'https://one.h5property.com/buildings/bam/apartments/19',
  '14D': 'https://one.h5property.com/buildings/bam/apartments/38',
  '14E': 'https://one.h5property.com/buildings/bam/apartments/26',
  '14F': 'https://one.h5property.com/buildings/bam/apartments/22',
  '14J': 'https://one.h5property.com/buildings/bam/apartments/25',
  '14K': 'https://one.h5property.com/buildings/bam/apartments/10',
  '14M': 'https://one.h5property.com/buildings/bam/apartments/24',
  '14Q': 'https://one.h5property.com/buildings/bam/apartments/18',
  '14R': 'https://one.h5property.com/buildings/bam/apartments/143',
  '14T': 'https://one.h5property.com/buildings/bam/apartments/20',
  '14U': 'https://one.h5property.com/buildings/bam/apartments/265',
  '15A': 'https://one.h5property.com/buildings/bam/apartments/162',
  '15B': 'https://one.h5property.com/buildings/bam/apartments/271',
  '15C': 'https://one.h5property.com/buildings/bam/apartments/264',
  '15D': 'https://one.h5property.com/buildings/bam/apartments/263',
  '15E': 'https://one.h5property.com/buildings/bam/apartments/262',
  '15F': 'https://one.h5property.com/buildings/bam/apartments/161',
  '15J': 'https://one.h5property.com/buildings/bam/apartments/141',
  '15K': 'https://one.h5property.com/buildings/bam/apartments/142',
  '15L': 'https://one.h5property.com/buildings/bam/apartments/261',
  '15M': 'https://one.h5property.com/buildings/bam/apartments/260',
  '15Q': 'https://one.h5property.com/buildings/bam/apartments/88',
  '15R': 'https://one.h5property.com/buildings/bam/apartments/259',
  '15T': 'https://one.h5property.com/buildings/bam/apartments/11',
  '15U': 'https://one.h5property.com/buildings/bam/apartments/132',
  '16A': 'https://one.h5property.com/buildings/bam/apartments/258',
  '16B': 'https://one.h5property.com/buildings/bam/apartments/147',
  '16C': 'https://one.h5property.com/buildings/bam/apartments/257',
  '16D': 'https://one.h5property.com/buildings/bam/apartments/99',
  '16E': 'https://one.h5property.com/buildings/bam/apartments/256',
  '16F': 'https://one.h5property.com/buildings/bam/apartments/122',
  '16J': 'https://one.h5property.com/buildings/bam/apartments/168',
  '16K': 'https://one.h5property.com/buildings/bam/apartments/255',
  '16L': 'https://one.h5property.com/buildings/bam/apartments/281',
  '16M': 'https://one.h5property.com/buildings/bam/apartments/134',
  '16N': 'https://one.h5property.com/buildings/bam/apartments/272',
  '16Q': 'https://one.h5property.com/buildings/bam/apartments/97',
  '16R': 'https://one.h5property.com/buildings/bam/apartments/144',
  '16T': 'https://one.h5property.com/buildings/bam/apartments/57',
  '16U': 'https://one.h5property.com/buildings/bam/apartments/171',
  '17A': 'https://one.h5property.com/buildings/bam/apartments/129',
  '17B': 'https://one.h5property.com/buildings/bam/apartments/145',
  '17C': 'https://one.h5property.com/buildings/bam/apartments/105',
  '17D': 'https://one.h5property.com/buildings/bam/apartments/113',
  '17E': 'https://one.h5property.com/buildings/bam/apartments/254',
  '17F': 'https://one.h5property.com/buildings/bam/apartments/253',
  '17J': 'https://one.h5property.com/buildings/bam/apartments/114',
  '17K': 'https://one.h5property.com/buildings/bam/apartments/274',
  '17L': 'https://one.h5property.com/buildings/bam/apartments/252',
  '17M': 'https://one.h5property.com/buildings/bam/apartments/251',
  '17N': 'https://one.h5property.com/buildings/bam/apartments/180',
  '17Q': 'https://one.h5property.com/buildings/bam/apartments/79',
  '17R': 'https://one.h5property.com/buildings/bam/apartments/301',
  '17T': 'https://one.h5property.com/buildings/bam/apartments/58',
  '17U': 'https://one.h5property.com/buildings/bam/apartments/101',
  '18A': 'https://one.h5property.com/buildings/bam/apartments/158',
  '18B': 'https://one.h5property.com/buildings/bam/apartments/276',
  '18C': 'https://one.h5property.com/buildings/bam/apartments/140',
  '18D': 'https://one.h5property.com/buildings/bam/apartments/182',
  '18E': 'https://one.h5property.com/buildings/bam/apartments/112',
  '18F': 'https://one.h5property.com/buildings/bam/apartments/250',
  '18J': 'https://one.h5property.com/buildings/bam/apartments/159',
  '18K': 'https://one.h5property.com/buildings/bam/apartments/86',
  '18L': 'https://one.h5property.com/buildings/bam/apartments/284',
  '18M': 'https://one.h5property.com/buildings/bam/apartments/170',
  '18N': 'https://one.h5property.com/buildings/bam/apartments/249',
  '18Q': 'https://one.h5property.com/buildings/bam/apartments/90',
  '18R': 'https://one.h5property.com/buildings/bam/apartments/98',
  '18T': 'https://one.h5property.com/buildings/bam/apartments/214',
  '18U': 'https://one.h5property.com/buildings/bam/apartments/248',
  '19A': 'https://one.h5property.com/buildings/bam/apartments/247',
  '19B': 'https://one.h5property.com/buildings/bam/apartments/179',
  '19C': 'https://one.h5property.com/buildings/bam/apartments/172',
  '19D': 'https://one.h5property.com/buildings/bam/apartments/149',
  '19E': 'https://one.h5property.com/buildings/bam/apartments/246',
  '19F': 'https://one.h5property.com/buildings/bam/apartments/174',
  '19J': 'https://one.h5property.com/buildings/bam/apartments/268',
  '19K': 'https://one.h5property.com/buildings/bam/apartments/166',
  '19L': 'https://one.h5property.com/buildings/bam/apartments/152',
  '19M': 'https://one.h5property.com/buildings/bam/apartments/75',
  '19N': 'https://one.h5property.com/buildings/bam/apartments/245',
  '19Q': 'https://one.h5property.com/buildings/bam/apartments/91',
  '19R': 'https://one.h5property.com/buildings/bam/apartments/78',
  '19T': 'https://one.h5property.com/buildings/bam/apartments/215',
  '19U': 'https://one.h5property.com/buildings/bam/apartments/131',
  '20A': 'https://one.h5property.com/buildings/bam/apartments/54',
  '20B': 'https://one.h5property.com/buildings/bam/apartments/137',
  '20C': 'https://one.h5property.com/buildings/bam/apartments/296',
  '20D': 'https://one.h5property.com/buildings/bam/apartments/87',
  '20E': 'https://one.h5property.com/buildings/bam/apartments/126',
  '20F': 'https://one.h5property.com/buildings/bam/apartments/73',
  '20K': 'https://one.h5property.com/buildings/bam/apartments/183',
  '20L': 'https://one.h5property.com/buildings/bam/apartments/175',
  '20M': 'https://one.h5property.com/buildings/bam/apartments/153',
  '20P': 'https://one.h5property.com/buildings/bam/apartments/244',
  '20R': 'https://one.h5property.com/buildings/bam/apartments/107',
  '20S': 'https://one.h5property.com/buildings/bam/apartments/243',
  '20T': 'https://one.h5property.com/buildings/bam/apartments/139',
  '20U': 'https://one.h5property.com/buildings/bam/apartments/290',
  '21A': 'https://one.h5property.com/buildings/bam/apartments/216',
  '21B': 'https://one.h5property.com/buildings/bam/apartments/242',
  '21C': 'https://one.h5property.com/buildings/bam/apartments/184',
  '21D': 'https://one.h5property.com/buildings/bam/apartments/163',
  '21E': 'https://one.h5property.com/buildings/bam/apartments/148',
  '21F': 'https://one.h5property.com/buildings/bam/apartments/285',
  '21K': 'https://one.h5property.com/buildings/bam/apartments/278',
  '21L': 'https://one.h5property.com/buildings/bam/apartments/181',
  '21M': 'https://one.h5property.com/buildings/bam/apartments/157',
  '21R': 'https://one.h5property.com/buildings/bam/apartments/77',
  '21S': 'https://one.h5property.com/buildings/bam/apartments/130',
  '21T': 'https://one.h5property.com/buildings/bam/apartments/50',
  '21U': 'https://one.h5property.com/buildings/bam/apartments/241',
  '22A': 'https://one.h5property.com/buildings/bam/apartments/64',
  '22B': 'https://one.h5property.com/buildings/bam/apartments/240',
  '22C': 'https://one.h5property.com/buildings/bam/apartments/280',
  '22D': 'https://one.h5property.com/buildings/bam/apartments/295',
  '22E': 'https://one.h5property.com/buildings/bam/apartments/239',
  '22F': 'https://one.h5property.com/buildings/bam/apartments/165',
  '22K': 'https://one.h5property.com/buildings/bam/apartments/297',
  '22L': 'https://one.h5property.com/buildings/bam/apartments/121',
  '22M': 'https://one.h5property.com/buildings/bam/apartments/56',
  '22R': 'https://one.h5property.com/buildings/bam/apartments/104',
  '22S': 'https://one.h5property.com/buildings/bam/apartments/238',
  '22T': 'https://one.h5property.com/buildings/bam/apartments/48',
  '22U': 'https://one.h5property.com/buildings/bam/apartments/273',
  '23A': 'https://one.h5property.com/buildings/bam/apartments/208',
  '23B': 'https://one.h5property.com/buildings/bam/apartments/237',
  '23C': 'https://one.h5property.com/buildings/bam/apartments/287',
  '23D': 'https://one.h5property.com/buildings/bam/apartments/288',
  '23E': 'https://one.h5property.com/buildings/bam/apartments/236',
  '23F': 'https://one.h5property.com/buildings/bam/apartments/298',
  '23K': 'https://one.h5property.com/buildings/bam/apartments/110',
  '23L': 'https://one.h5property.com/buildings/bam/apartments/108',
  '23M': 'https://one.h5property.com/buildings/bam/apartments/209',
  '23R': 'https://one.h5property.com/buildings/bam/apartments/135',
  '23S': 'https://one.h5property.com/buildings/bam/apartments/185',
  '23T': 'https://one.h5property.com/buildings/bam/apartments/156',
  '23U': 'https://one.h5property.com/buildings/bam/apartments/115',
  '24A': 'https://one.h5property.com/buildings/bam/apartments/212',
  '24B': 'https://one.h5property.com/buildings/bam/apartments/128',
  '24C': 'https://one.h5property.com/buildings/bam/apartments/76',
  '24D': 'https://one.h5property.com/buildings/bam/apartments/235',
  '24E': 'https://one.h5property.com/buildings/bam/apartments/289',
  '24F': 'https://one.h5property.com/buildings/bam/apartments/294',
  '24K': 'https://one.h5property.com/buildings/bam/apartments/300',
  '24L': 'https://one.h5property.com/buildings/bam/apartments/169',
  '24N': 'https://one.h5property.com/buildings/bam/apartments/127',
  '24R': 'https://one.h5property.com/buildings/bam/apartments/269',
  '24S': 'https://one.h5property.com/buildings/bam/apartments/302',
  '24T': 'https://one.h5property.com/buildings/bam/apartments/46',
  '24U': 'https://one.h5property.com/buildings/bam/apartments/176',
  '25A': 'https://one.h5property.com/buildings/bam/apartments/68',
  '25B': 'https://one.h5property.com/buildings/bam/apartments/100',
  '25C': 'https://one.h5property.com/buildings/bam/apartments/234',
  '25D': 'https://one.h5property.com/buildings/bam/apartments/103',
  '25E': 'https://one.h5property.com/buildings/bam/apartments/282',
  '25F': 'https://one.h5property.com/buildings/bam/apartments/118',
  '25K': 'https://one.h5property.com/buildings/bam/apartments/177',
  '25L': 'https://one.h5property.com/buildings/bam/apartments/277',
  '25N': 'https://one.h5property.com/buildings/bam/apartments/217',
  '25R': 'https://one.h5property.com/buildings/bam/apartments/293',
  '25S': 'https://one.h5property.com/buildings/bam/apartments/133',
  '25T': 'https://one.h5property.com/buildings/bam/apartments/211',
  '25U': 'https://one.h5property.com/buildings/bam/apartments/233',
  '26A': 'https://one.h5property.com/buildings/bam/apartments/154',
  '26B': 'https://one.h5property.com/buildings/bam/apartments/178',
  '26C': 'https://one.h5property.com/buildings/bam/apartments/266',
  '26D': 'https://one.h5property.com/buildings/bam/apartments/279',
  '26E': 'https://one.h5property.com/buildings/bam/apartments/275',
  '26F': 'https://one.h5property.com/buildings/bam/apartments/173',
  '26K': 'https://one.h5property.com/buildings/bam/apartments/232',
  '26L': 'https://one.h5property.com/buildings/bam/apartments/138',
  '26N': 'https://one.h5property.com/buildings/bam/apartments/111',
  '26R': 'https://one.h5property.com/buildings/bam/apartments/89',
  '26S': 'https://one.h5property.com/buildings/bam/apartments/117',
  '26T': 'https://one.h5property.com/buildings/bam/apartments/63',
  '26U': 'https://one.h5property.com/buildings/bam/apartments/231',
  '27A': 'https://one.h5property.com/buildings/bam/apartments/51',
  '27B': 'https://one.h5property.com/buildings/bam/apartments/155',
  '27C': 'https://one.h5property.com/buildings/bam/apartments/230',
  '27D': 'https://one.h5property.com/buildings/bam/apartments/291',
  '27E': 'https://one.h5property.com/buildings/bam/apartments/167',
  '27F': 'https://one.h5property.com/buildings/bam/apartments/283',
  '27K': 'https://one.h5property.com/buildings/bam/apartments/229',
  '27L': 'https://one.h5property.com/buildings/bam/apartments/124',
  '27N': 'https://one.h5property.com/buildings/bam/apartments/116',
  '27R': 'https://one.h5property.com/buildings/bam/apartments/74',
  '27S': 'https://one.h5property.com/buildings/bam/apartments/164',
  '27T': 'https://one.h5property.com/buildings/bam/apartments/218',
  '27U': 'https://one.h5property.com/buildings/bam/apartments/270',
  '28A': 'https://one.h5property.com/buildings/bam/apartments/65',
  '28B': 'https://one.h5property.com/buildings/bam/apartments/125',
  '28C': 'https://one.h5property.com/buildings/bam/apartments/186',
  '28D': 'https://one.h5property.com/buildings/bam/apartments/102',
  '28E': 'https://one.h5property.com/buildings/bam/apartments/303',
  '28F': 'https://one.h5property.com/buildings/bam/apartments/106',
  '28K': 'https://one.h5property.com/buildings/bam/apartments/286',
  '28L': 'https://one.h5property.com/buildings/bam/apartments/292',
  '28N': 'https://one.h5property.com/buildings/bam/apartments/213',
  '28R': 'https://one.h5property.com/buildings/bam/apartments/109',
  '28S': 'https://one.h5property.com/buildings/bam/apartments/299',

  '28U': 'https://one.h5property.com/buildings/bam/apartments/120',
  '30A': 'https://one.h5property.com/buildings/bam/apartments/160',
  '30B': 'https://one.h5property.com/buildings/bam/apartments/55',
  '30C': 'https://one.h5property.com/buildings/bam/apartments/119',
  '30D': 'https://one.h5property.com/buildings/bam/apartments/220',
  '30E': 'https://one.h5property.com/buildings/bam/apartments/228',
  '30F': 'https://one.h5property.com/buildings/bam/apartments/227',
  '30G': 'https://one.h5property.com/buildings/bam/apartments/96',
  '30H': 'https://one.h5property.com/buildings/bam/apartments/62',
  '30J': 'https://one.h5property.com/buildings/bam/apartments/80',
  '31A': 'https://one.h5property.com/buildings/bam/apartments/61',
  '31B': 'https://one.h5property.com/buildings/bam/apartments/60',
  '31C': 'https://one.h5property.com/buildings/bam/apartments/81',
  '31D': 'https://one.h5property.com/buildings/bam/apartments/221',
  '31E': 'https://one.h5property.com/buildings/bam/apartments/226',
  '31F': 'https://one.h5property.com/buildings/bam/apartments/207',
  '31G': 'https://one.h5property.com/buildings/bam/apartments/92',
  '31H': 'https://one.h5property.com/buildings/bam/apartments/206',
  '31J': 'https://one.h5property.com/buildings/bam/apartments/205',
  '32A': 'https://one.h5property.com/buildings/bam/apartments/222',
  '32B': 'https://one.h5property.com/buildings/bam/apartments/223',
  '32C': 'https://one.h5property.com/buildings/bam/apartments/204',
  '32D': 'https://one.h5property.com/buildings/bam/apartments/203',
  '32E': 'https://one.h5property.com/buildings/bam/apartments/202',
  '32G': 'https://one.h5property.com/buildings/bam/apartments/93',
  '32H': 'https://one.h5property.com/buildings/bam/apartments/82',
  '32J': 'https://one.h5property.com/buildings/bam/apartments/83',
  '33A': 'https://one.h5property.com/buildings/bam/apartments/59',
  '33B': 'https://one.h5property.com/buildings/bam/apartments/224',
  '33C': 'https://one.h5property.com/buildings/bam/apartments/201',
  '33D': 'https://one.h5property.com/buildings/bam/apartments/200',
  '33E': 'https://one.h5property.com/buildings/bam/apartments/199',
  '33G': 'https://one.h5property.com/buildings/bam/apartments/94',
  '33H': 'https://one.h5property.com/buildings/bam/apartments/225',
  '33J': 'https://one.h5property.com/buildings/bam/apartments/198',
  '34A': 'https://one.h5property.com/buildings/bam/apartments/197',
  '34B': 'https://one.h5property.com/buildings/bam/apartments/196',
  '34C': 'https://one.h5property.com/buildings/bam/apartments/195',
  '34D': 'https://one.h5property.com/buildings/bam/apartments/194',
  '34E': 'https://one.h5property.com/buildings/bam/apartments/193',
  '34G': 'https://one.h5property.com/buildings/bam/apartments/95',
  '34H': 'https://one.h5property.com/buildings/bam/apartments/84',
  '34J': 'https://one.h5property.com/buildings/bam/apartments/85',
  '35A': 'https://one.h5property.com/buildings/bam/apartments/69',
  '35B': 'https://one.h5property.com/buildings/bam/apartments/67',
  '35C': 'https://one.h5property.com/buildings/bam/apartments/192',
  '35D': 'https://one.h5property.com/buildings/bam/apartments/42',
  '35E': 'https://one.h5property.com/buildings/bam/apartments/191',
  '35G': 'https://one.h5property.com/buildings/bam/apartments/151',
  '35H': 'https://one.h5property.com/buildings/bam/apartments/53',
  '35J': 'https://one.h5property.com/buildings/bam/apartments/146',
  PHA: 'https://one.h5property.com/buildings/bam/apartments/43',
  PHB: 'https://one.h5property.com/buildings/bam/apartments/44',
  PHC: 'https://one.h5property.com/buildings/bam/apartments/190',
  PHD: 'https://one.h5property.com/buildings/bam/apartments/45',
  PHE: 'https://one.h5property.com/buildings/bam/apartments/189',
  PHF: 'https://one.h5property.com/buildings/bam/apartments/188',
  PHG: 'https://one.h5property.com/buildings/bam/apartments/150',
  PHH: 'https://one.h5property.com/buildings/bam/apartments/52',
  PHJ: 'https://one.h5property.com/buildings/bam/apartments/187',
};

function getSavedListings() {
  return getCookie('saved_units') || [];
}

function setSavedListings(newSavedUnits) {
  setCookie('saved_units', newSavedUnits, 30);
  return true;
}

// Use cookies to store saved listings.
// This approach was here when I arrived...and it kinda makes sense,
// though it's far less elegant than localStorage.
// On the server (when there's no document), we return undefined.
function getCookie(name) {
  if (typeof document === 'undefined') {
    return undefined;
  }

  var re = new RegExp(name + '=([^;]+)');
  var value = re.exec(document.cookie); // eslint-disable-line no-undef
  var ret = value != null ? unescape(value[1]) : null;
  return JSON.parse(ret);
}

function setCookie(cookieName, cookieValue, nDays) {
  var today = new Date(),
    expire = new Date(),
    json_str = JSON.stringify(cookieValue);

  expire.setTime(today.getTime() + 3600000 * 24 * (nDays || 1));

  /* eslint-disable no-undef */
  if (typeof document !== 'undefined' && document.cookie) {
    document.cookie =
      cookieName + '=' + json_str + ';expires=' + expire.toGMTString();
  }
  /* eslint-enable no-undef */
}



// WEBPACK FOOTER //
// ./client/services/listings.js